//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ConditionalWrapper from '@components/ConditionalWrapper';
import {
  CaptionPrimaryAnchor,
  PrimaryAnchor,
} from '@components/atoms/anchor/Anchor';
import { FACEBOOK_SCHEDULE_TRACKING_IDS } from '@facebook/constants/facebookConstants';
import { isDefined } from '@utils/filterPredicates';
import {
  createFacebookHashtagLink,
  createFacebookPageMentionLink,
  extractFacebookPageIdFromTag,
} from '@utils/social-text-utils/facebookTextUtils';
import {
  SocialTextEntityType,
  ISocialTextEntity,
} from '@utils/social-text-utils/socialTextUtilsModel';
import { getUrlWithSecureProtocol } from '@utils/urlUtils';

interface IFacebookSegmentProps {
  entity: ISocialTextEntity;
  isWeekView: boolean;
}

type FacebookSegmentProps = IFacebookSegmentProps;

const FacebookTextSegment: React.FunctionComponent<FacebookSegmentProps> = ({
  entity,
  isWeekView,
}) => {
  const taggedPages = useSelector(
    (state) => state.facebook.schedule.taggedPages.data,
  );

  const facebookPage = useMemo(() => {
    if (entity.type == SocialTextEntityType.FacebookPageMention) {
      const page = PojoMap.get(
        taggedPages,
        extractFacebookPageIdFromTag(entity.value),
      );

      if (!isDefined(page)) {
        return {
          link: createFacebookPageMentionLink(entity.value),
          name: entity.value,
        };
      }

      return page;
    }
  }, [entity.type, entity.value, taggedPages]);

  const href = useMemo(() => {
    switch (entity.type) {
      case SocialTextEntityType.FacebookPageMention:
        return facebookPage?.link;
      case SocialTextEntityType.Hashtag:
        return createFacebookHashtagLink(entity.value);
      case SocialTextEntityType.Url:
        // Add protocol so links don't become relative to page location
        return getUrlWithSecureProtocol(entity.value);
      default:
        return undefined;
    }
  }, [entity.value, entity.type, facebookPage]);

  const trackingId = useMemo(() => {
    switch (entity.type) {
      case SocialTextEntityType.FacebookPageMention:
        return FACEBOOK_SCHEDULE_TRACKING_IDS.PAGE_TAG_LINK;
      case SocialTextEntityType.Hashtag:
        return FACEBOOK_SCHEDULE_TRACKING_IDS.HASHTAG_LINK;
      case SocialTextEntityType.Url:
        return FACEBOOK_SCHEDULE_TRACKING_IDS.URL;
      default:
        undefined;
    }
  }, [entity.type]);

  return (
    <ConditionalWrapper
      // If the href for a FacebookPageMention is not defined, this means that the page
      // was not successfully retrieved so we should not wrap the page mention.
      shouldWrap={!!href}
      renderWrap={(children) =>
        isWeekView ? (
          <WeekViewAnchor target='_blank' href={href} trackingId={trackingId}>
            {children}
          </WeekViewAnchor>
        ) : (
          <PrimaryAnchor target='_blank' href={href} trackingId={trackingId}>
            {children}
          </PrimaryAnchor>
        )
      }
    >
      {/* If there is a facebook page, we want to show that page name, otherwise
          we want to show the plain text */}
      {facebookPage?.name ?? entity.value}
    </ConditionalWrapper>
  );
};

const WeekViewAnchor = styled(CaptionPrimaryAnchor)(
  ({ theme }) => `
  && {
    color: ${theme.colors.aliases.primary};
  }
`,
);

export default FacebookTextSegment;
